/**
 * Sharing, two shapes, one Web Share API path:
 *
 *   1. The STORY CARD (spec 2026-09-09 §1b): a branded PNG of this swim or this
 *      set plus its story line — what the header's Share control sends. The
 *      card model is the frozen ShareCard v1 (shareCard/types.ts); the sheet
 *      picks the SOURCE from what is on screen (`shareSourceFor`) and applies
 *      the two toggles (`applyShareToggles`) before rendering.
 *   2. The FIREHOSE EXPORT: the raw HealthKit dump as JSON, unchanged from the
 *      day it shipped ("Export data (JSON)" in the menu and the share sheet).
 *
 * Both try `navigator.share` with a file first (iOS 15+ Safari, WKWebView) and
 * fall back to a blob-URL download when the API or files are unsupported.
 *
 * HYG-04: every export is wrapped in a versioned FirehoseExportEnvelope so a
 * companion import (Phase 6, cross-device over CloudKit) can never silently
 * consume a mismatched payload. `parseImport` below is the untrusted-input
 * boundary that reads it back.
 */
import type {
  FirehoseExportEnvelope,
  SwimFirehoseResponse,
} from "@contract/types";
import { SwimFirehoseResponseSchema } from "../bridge/validate.generated";
import type { TabId } from "./nav";
import type { DerivedWorkout } from "./lapAnalysis";
import type { SetGroup } from "./setGroups";
import type { ShareCard, ShareSet } from "./shareCard/types";
import { encodeShareCard } from "./shareCard/codec";
import { heroSetOf, storyForCard } from "./shareCard";
import { fmtMeters } from "./format";
import { writable } from "svelte/store";
import { openOverlay } from "./overlay";

/**
 * Current export schema version. Bump ONLY on a breaking change to
 * SwimFirehoseResponse's shape. A no-envelope file (the existing May-11 raw
 * export) is treated as v0 by `parseImport` for backward tolerance.
 */
export const CURRENT_SCHEMA_VERSION = 1;

/**
 * Upper bound on the raw import in UTF-8 BYTES before we attempt to parse it. A
 * real firehose is ~170 KB; this cap is generous (8 MB) so legitimate large
 * swims still import, but bounds an attacker-sized blob BEFORE JSON.parse so a
 * companion file can't DoS the parser (T-05-04a). Measured in true UTF-8 bytes
 * via TextEncoder — NOT `string.length` (UTF-16 code units), which UNDER-counts
 * multibyte text (a CJK char is length 1 but 3 UTF-8 bytes) and would let a
 * multibyte payload bypass the cap ~3x.
 */
const MAX_IMPORT_BYTES = 8 * 1024 * 1024;

/**
 * Phase-6 content caps (security pre-bakes, T-06-01a / T-06-01b / T-06-01d).
 * These bound a payload that is UNDER the 8 MB byte cap but still pathological
 * — e.g. a 7.9 MB file with millions of tiny samples that would DoS the overlay
 * aligner / ECharts. The generated SwimFirehoseResponseSchema is content-only
 * and cannot carry these (its source is the codegen contract, not hand-edited),
 * so they are enforced HERE after shape-validation as typed parseImport results.
 *
 * Caps are generous for a real swim: a 1-hour swim sampling HR at ~1 Hz is
 * ~3600 samples/bucket; 100k leaves orders of magnitude of headroom while still
 * bounding an attacker. events/activities are per-workout structural counts that
 * realistically number in the hundreds, so 50k / 5k are deliberately loose.
 */
const MAX_SAMPLES_PER_BUCKET = 100_000;
const MAX_EVENTS = 50_000;
const MAX_ACTIVITIES = 5_000;
/**
 * Metadata bag bound (T-06-01b). A real HK sample/event metadata bag carries a
 * handful of keys (HKSWOLFScore, HKSwimmingStrokeStyle, …). 256 keys is far
 * above any legitimate bag while bounding a key-count-flood; depth 8 bounds a
 * pathologically-nested bag. parseImport already strips reserved keys + clones;
 * this adds a SIZE bound on top so an unbounded record can't slip the cap.
 */
const MAX_METADATA_KEYS = 256;
const MAX_METADATA_DEPTH = 8;

/**
 * Build the versioned export envelope around a firehose response. Single
 * source of truth for the wrapped shape — both `shareFirehose` (here) and the
 * Prototypes "Share this swim's data" buttons serialize via this.
 */
export function buildExportEnvelope(
  fh: SwimFirehoseResponse,
): FirehoseExportEnvelope {
  // Exporter provenance (lane swim-data-corpus): which reader wrote these bytes and when
  // they left the phone. Absent readerVersion = a reader from before provenance (v1).
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    payload: fh,
    provenance: { readerVersion: fh.readerVersion ?? 1, exportedAt: new Date().toISOString() },
  };
}

/** Serialize a firehose into the canonical pretty-printed export JSON string. */
export function serializeExport(fh: SwimFirehoseResponse): string {
  return JSON.stringify(buildExportEnvelope(fh), null, 2);
}

export type ShareResult =
  | { kind: "shared" }
  | { kind: "downloaded" }
  | { kind: "cancelled" }
  | { kind: "error"; message: string };

/**
 * Typed result of `parseImport`. Untrusted input is NEVER thrown on — every
 * rejection is a typed { ok:false, code, message } so the Phase-6 companion
 * caller handles a stale/mismatched/attacker file gracefully (T-05-04d).
 */
export type ImportResult =
  | { ok: true; payload: SwimFirehoseResponse }
  | { ok: false; code: ImportErrorCode; message: string };

export type ImportErrorCode =
  | "TOO_LARGE"
  | "MALFORMED"
  | "UNSUPPORTED_SCHEMA_VERSION"
  | "INVALID_SHAPE";

function filenameFor(fh: SwimFirehoseResponse): string {
  const date = new Date(fh.workout.startDate).toISOString().slice(0, 10);
  return `swim-firehose-${date}.json`;
}

function summaryText(fh: SwimFirehoseResponse): string {
  const date = new Date(fh.workout.startDate).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const distance =
    fh.workout.totalDistance?.value != null
      ? `${Math.round(fh.workout.totalDistance.value)} m`
      : "";
  const duration = Math.round(fh.workout.durationSeconds / 60);
  return [
    `Swim Set Club · ${date}`,
    distance && `${distance}, ${duration} min`,
  ]
    .filter(Boolean)
    .join(" — ");
}

/**
 * One file through the Web Share API, else a blob-URL download. `extra` is the
 * rest of the ShareData (title, text, url) the caller wants alongside the file.
 * `withFile: false` leaves the `files` key out entirely (the share chips, Image
 * off); the download fallback still saves the file, the only thing it can do.
 */
async function shareBlob(
  blob: Blob,
  filename: string,
  extra: Omit<ShareData, "files">,
  withFile = true,
): Promise<ShareResult> {
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
  };

  if (typeof nav.share === "function") {
    const shareData: ShareData = withFile
      ? { files: [new File([blob], filename, { type: blob.type })], ...extra }
      : { ...extra };
    if (!nav.canShare || nav.canShare(shareData)) {
      try {
        await nav.share(shareData);
        return { kind: "shared" };
      } catch (err) {
        // AbortError = user cancelled the share sheet
        const msg = err instanceof Error ? err.message : String(err);
        if (err instanceof Error && err.name === "AbortError") {
          return { kind: "cancelled" };
        }
        return { kind: "error", message: msg };
      }
    }
  }

  // Fallback: download via blob URL.
  try {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { kind: "downloaded" };
  } catch (err) {
    return {
      kind: "error",
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

export async function shareFirehose(
  fh: SwimFirehoseResponse,
): Promise<ShareResult> {
  // serializeExport (JSON.stringify) can throw on a pathological payload — it
  // used to sit outside every guarded branch, so a throw escaped shareFirehose
  // entirely and the caller showed nothing. Guard it here into a typed error
  // result so the ShareSheet's existing "Couldn't share" status renders.
  let json: string;
  try {
    json = serializeExport(fh);
  } catch {
    return {
      kind: "error",
      message: "this swim's data couldn't be packaged to share.",
    };
  }
  const blob = new Blob([json], { type: "application/json" });
  return shareBlob(blob, filenameFor(fh), { title: "Swim firehose", text: summaryText(fh) });
}

// ---------------------------------------------------------------------------
// The story card (spec §1b).

/**
 * What the Share control shares, decided by what is on screen:
 *   Swim tab   → this swim;   Scores tab → this swim's set;
 *   Club tab   → nothing yet (disabled, with a hint);
 *   no swim loaded on Swim/Scores → empty, with a hint.
 */
export type ShareSource =
  | { kind: "swim"; title: "Share this swim" }
  | { kind: "set"; title: "Share this set" }
  | { kind: "empty"; title: "Share"; hint: string }
  | { kind: "disabled"; title: "Share"; hint: string };

export function shareSourceFor(tab: TabId, hasSwim: boolean): ShareSource {
  if (tab === "club") {
    return {
      kind: "disabled",
      title: "Share",
      hint: "Nothing to share from the Club yet. Share a swim from Swim, or its set from Scores.",
    };
  }
  if (!hasSwim) {
    return { kind: "empty", title: "Share", hint: "No swim loaded yet. Pick a swim first." };
  }
  return tab === "swim"
    ? { kind: "swim", title: "Share this swim" }
    : { kind: "set", title: "Share this set" };
}

/**
 * "This swim's set" on the Scores tab: the set the card is about. Mirrors
 * share-card's `heroSetOf` fallback at SetGroup level — the biggest main-role
 * set (not warmup/cooldown, not a lone length), else the biggest set of all;
 * null when the swim has no sets. Lives here, not in the stub, so the rule
 * survives the stub's deletion.
 */
export function heroSetGroup(dw: DerivedWorkout): SetGroup | null {
  if (dw.setGroups.length === 0) return null;
  const main = dw.setGroups.filter(
    (g) => g.setId !== null && g.setId !== "warmup" && g.setId !== "cooldown" && !g.isLoneLength,
  );
  const pool = main.length > 0 ? main : dw.setGroups;
  return pool.reduce((a, b) => (b.meters > a.meters ? b : a));
}

/**
 * The SetGroup behind the swim card's hero set, so the Scores tab shares the
 * SAME set the Swim story is about (their `heroSetOf` picks the set the story
 * line names first; the size fallback above only agrees when no set is named).
 * Matched by label and rounded metres, since labels can repeat within a swim
 * ("Freestyle set" twice); falls back to heroSetGroup.
 */
export function heroSetGroupFor(dw: DerivedWorkout, hero: { name: string; totalM: number } | null): SetGroup | null {
  if (hero) {
    const match = dw.setGroups.find((g) => g.label === hero.name && Math.round(g.meters) === hero.totalM);
    if (match) return match;
  }
  return heroSetGroup(dw);
}

/**
 * Scores → Your Sets → "Share this set": the setId the share sheet should
 * build a set card for, whatever tab it opens on. null = share what is on
 * screen. The sheet clears it when it closes.
 */
export const shareSetRequest = writable<string | null>(null);

/** Open the share sheet on one set's card (Scott, 06:17: "share this set" from Your Sets). */
export function requestSetShare(setId: string): void {
  shareSetRequest.set(setId);
  openOverlay({ kind: "share" });
}

/**
 * The SetGroup a Your Sets share is drawn from: the LAST occurrence of `setId`
 * in this swim (two Rockalitas in one swim share the later one), or null when
 * the swim never swam it.
 */
export function setGroupForShare(dw: DerivedWorkout, setId: string): SetGroup | null {
  for (let i = dw.setGroups.length - 1; i >= 0; i--) {
    if (dw.setGroups[i].setId === setId) return dw.setGroups[i];
  }
  return null;
}

/** What the sheet shows for a card: a preview it can draw, or why it could not. */
export type PreviewResult =
  | { ok: true; blob: Blob; url: string }
  | { ok: false; message: string };

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error ?? new Error("read failed"));
    r.readAsDataURL(blob);
  });
}

/**
 * Render the 9:16 story card and turn it into something an <img> can show.
 * NEVER throws: a renderer that rejects (no 2D canvas, an SVG that will not
 * load, a PNG encode that returns null) becomes `{ ok: false, message }` so the
 * sheet can say so and offer a retry instead of hanging on "Drawing your card…".
 *
 * The preview is a data: URL, not a blob: URL: index.html's CSP is
 * `img-src 'self' data:` and Chrome refuses blob: images outright (measured
 * 2026-09-09 23:03).
 */
export async function buildPreview(
  card: ShareCard,
  render: (card: ShareCard, size: "story") => Promise<Blob>,
): Promise<PreviewResult> {
  try {
    const blob = await render(card, "story");
    const url = await blobToDataUrl(blob);
    return { ok: true, blob, url };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

/**
 * The sheet's kicker: what is being shared AND what it covers. A set card
 * names its set — "Share this set · The Rockalita" (VP round 2, items 1-2: the
 * story line alone never named the set). A swim card's hero is the whole swim
 * (Scott, 2026-09-10 05:56), so it names the swim's distance, not one set:
 * "Share this swim · 2,000 m".
 */
export function shareKicker(source: ShareSource, card: ShareCard | null): string {
  if (!card) return source.title;
  return card.kind === "swim" ? `${source.title} · ${fmtMeters(card.distanceM)}` : `${source.title} · ${card.set.name}`;
}

/** The sheet's two switches. Both default on. */
export interface ShareToggles {
  sets: boolean;
  heartRate: boolean;
}

/**
 * The card with the toggles applied: Sets off keeps ONLY the hero set (the one
 * the story is about — the story must never name a set the card does not
 * draw; VP round 2, item 3) and drops the rest of the swim's sets. Heart rate
 * off drops every heart-rate field on either kind: the swim's `avgHr` and each
 * set's `hr`, so no avg/peak, no Hardest mark and no honesty line is drawn. A
 * set card has no set list, so Sets does nothing to it, and with nothing to
 * drop it comes back as the same object. Never mutates the input.
 */
export function applyShareToggles(card: ShareCard, t: ShareToggles): ShareCard {
  const withoutHr = (s: ShareSet): ShareSet => {
    const { hr: _hr, ...rest } = s;
    return rest;
  };
  if (card.kind === "set") return t.heartRate || !card.set.hr ? card : { ...card, set: withoutHr(card.set) };
  const { avgHr, ...rest } = card;
  const hero = heroSetOf(card);
  const sets = t.sets ? card.sets : hero ? [hero] : [];
  const out: ShareCard = { ...rest, sets: t.heartRate ? sets : sets.map(withoutHr) };
  if (t.heartRate && avgHr !== undefined) out.avgHr = avgHr;
  return out;
}

function slug(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "set";
}

/** `swim-set-club-2026-09-09.png` for a swim, `swim-set-club-the-rockalita.png` for a set. */
export function shareCardFilename(card: ShareCard): string {
  return card.kind === "swim"
    ? `swim-set-club-${card.date}.png`
    : `swim-set-club-${slug(card.set.name)}.png`;
}

/** The text line that rides beside the image: the story as the card draws it
 *  (a set card names its owner, item 6), then the product. */
export function shareCardText(card: ShareCard): string {
  const story = storyForCard(card);
  const line = story.detail ? `${story.text} — ${story.detail}` : story.text;
  return `${line} · Swim Set Club`;
}

/**
 * Where story links live (contract C2). The app stays on swimset.club; the
 * worker serves `/s/<kind>/<encoded>` on this host and swimset.club 302s
 * there. No storage: the link IS the card.
 */
export const STORY_ORIGIN = "https://swimsetclub.com";

/** `https://swimsetclub.com/s/swim/1.…` — the encoded card as the path segment. */
export async function storyUrl(card: ShareCard): Promise<string> {
  return `${STORY_ORIGIN}/s/${card.kind}/${await encodeShareCard(card)}`;
}

/**
 * "What goes out" (lane share-chips, 2026-09-12). Scott: "it's a lot when you
 * share something, to put three links into text." Three chips on the sheet,
 * one per item a share can hand iOS: Image = the card PNG (`files`), Link =
 * the story URL (`url`), Story = the one-line story text (`text`).
 */
export interface ShareItems {
  image: boolean;
  link: boolean;
  story: boolean;
}
export type ShareItemKey = keyof ShareItems;

/** Image only, every time the sheet opens (Scott's ruling; no stored preference). */
export const DEFAULT_SHARE_ITEMS: Readonly<ShareItems> = Object.freeze({ image: true, link: false, story: false });

/**
 * The chips in sheet order. Each sublabel is a noun for what the item IS (VP
 * round 3): no quoted story line, which ellipsized mid-word and repeated the
 * headline a third time. Every sublabel must fit its chip at 390 px uncut
 * (scripts/verify-share-chips-fit.mjs measures it).
 */
export const SHARE_ITEM_CHIPS: ReadonlyArray<{ key: ShareItemKey; text: string; hint: string }> = [
  { key: "image", text: "Image", hint: "the card" },
  { key: "link", text: "Link", hint: "a page to open" },
  { key: "story", text: "Story", hint: "headline as text" },
];

/**
 * Flip one chip. The last lit chip cannot be turned off, so a share never
 * carries nothing: that tap returns the SAME object, which the chip row reads
 * as "refused". Never mutates the input.
 */
export function toggleShareItem(items: ShareItems, key: ShareItemKey): ShareItems {
  const lit = SHARE_ITEM_CHIPS.filter((c) => items[c.key]).length;
  if (items[key] && lit === 1) return items;
  return { ...items, [key]: !items[key] };
}

/** The header's live summary: "image", "image · link", "link · story", … */
export function shareItemsSummary(items: ShareItems): string {
  return SHARE_ITEM_CHIPS.filter((c) => items[c.key])
    .map((c) => c.text.toLowerCase())
    .join(" · ");
}

/**
 * Share the rendered story card, only the items the chips chose: the PNG as a
 * file (Image), the story link (Link), the story text (Story). An item that is
 * off is not an empty value, its key is absent. `title` always rides along: it
 * is not one of the three items (Mail uses it as the subject; Messages draws
 * nothing for it). `png` is what `renderShareCard(card, "story")` returned (the
 * 9:16 Stories size — the one image the sheet previews and sends).
 */
export async function shareStoryCard(card: ShareCard, png: Blob, items: ShareItems): Promise<ShareResult> {
  const extra: Omit<ShareData, "files"> = { title: card.kind === "swim" ? "My swim" : card.set.name };
  if (items.story) extra.text = shareCardText(card);
  if (items.link) extra.url = await storyUrl(card);
  return shareBlob(png, shareCardFilename(card), extra, items.image);
}

/**
 * Reserved keys that, if present as own keys on a metadata bag, are a
 * prototype-pollution risk the moment a downstream sink spreads/indexes the
 * bag. We strip them from every metadata bag so a companion file (Phase 6,
 * cross-device over CloudKit) cannot smuggle them past `parseImport`.
 */
const RESERVED_KEYS = ["__proto__", "constructor", "prototype"] as const;

/**
 * Strip the reserved keys from a single metadata bag IN PLACE. The bag is a
 * `z.record(z.string(), z.unknown())` (nullable/optional), so guard for
 * null/undefined and non-object values. A literal own `"__proto__"` key from
 * JSON.parse survives structuredClone, so delete the reserved OWN keys
 * explicitly rather than trusting the clone alone.
 */
function stripReservedKeys(bag: unknown): void {
  if (typeof bag !== "object" || bag === null) return;
  for (const key of RESERVED_KEYS) {
    if (Object.prototype.hasOwnProperty.call(bag, key)) {
      delete (bag as Record<string, unknown>)[key];
    }
  }
}

/**
 * Build a sanitized, fully-owned clone of a validated firehose payload. We
 * NEVER return the safeParse output by reference — that aliases the untrusted
 * parse graph. structuredClone severs the alias; then we strip reserved keys
 * from every metadata bag (top-level events, per-activity events, and bucket
 * samples). Pure content sanitizer — no provenance/identity/CloudKit awareness
 * (that is Phase 6).
 */
function sanitizePayload(payload: SwimFirehoseResponse): SwimFirehoseResponse {
  const clone = structuredClone(payload);

  for (const event of clone.events ?? []) {
    stripReservedKeys(event?.metadata);
  }
  for (const activity of clone.activities ?? []) {
    for (const event of activity?.events ?? []) {
      stripReservedKeys(event?.metadata);
    }
  }
  for (const bucket of clone.buckets ?? []) {
    for (const sample of bucket?.samples ?? []) {
      stripReservedKeys(sample?.metadata);
    }
  }

  return clone;
}

/**
 * ISO-8601 check (T-06-01d). The generated schema validates a date field as a
 * bare `z.string()` — a non-date string ("not-a-date") would pass shape but is
 * meaningless and can crash a downstream `new Date(...)` consumer with NaN. We
 * reject any date string that does not parse to a finite epoch. `Date.parse`
 * accepts the ISO-8601 forms the firehose emits (with `Z`/offset) and rejects
 * gibberish; we keep it deliberately lenient on FORMAT (any parseable date) but
 * strict on PARSEABILITY, since the firehose is the only producer.
 */
function isIsoDate(s: string): boolean {
  return Number.isFinite(Date.parse(s));
}

/**
 * Bound a metadata bag (T-06-01b): reject if it exceeds the key-count or
 * nesting-depth bound. Returns the offending reason or null if within bounds.
 * Walks own enumerable keys only (the bag came through JSON.parse).
 */
function metadataWithinBounds(
  bag: unknown,
  depth = 0,
): boolean {
  if (typeof bag !== "object" || bag === null) return true;
  if (depth > MAX_METADATA_DEPTH) return false;
  const keys = Object.keys(bag as Record<string, unknown>);
  if (keys.length > MAX_METADATA_KEYS) return false;
  for (const k of keys) {
    if (!metadataWithinBounds((bag as Record<string, unknown>)[k], depth + 1)) {
      return false;
    }
  }
  return true;
}

/**
 * Enforce the Phase-6 content caps + ISO date + metadata bounds (T-06-01a/b/d)
 * on a SHAPE-VALID payload. Returns null when the content is within bounds, or a
 * typed ImportResult failure otherwise — NEVER a throw. Runs AFTER
 * SwimFirehoseResponseSchema.safeParse and BEFORE the consumer ever walks the
 * payload, so an under-byte-cap-but-pathological file is rejected loudly here.
 * Pure content validation — no identity/provenance awareness (T-06-01c).
 */
function hardenContent(
  fh: SwimFirehoseResponse,
): { ok: false; code: ImportErrorCode; message: string } | null {
  // Array-length caps (T-06-01a).
  if ((fh.events?.length ?? 0) > MAX_EVENTS) {
    return {
      ok: false,
      code: "TOO_LARGE",
      message: `events array has ${fh.events.length} entries; max is ${MAX_EVENTS}.`,
    };
  }
  if ((fh.activities?.length ?? 0) > MAX_ACTIVITIES) {
    return {
      ok: false,
      code: "TOO_LARGE",
      message: `activities array has ${fh.activities.length} entries; max is ${MAX_ACTIVITIES}.`,
    };
  }
  for (const bucket of fh.buckets ?? []) {
    if ((bucket.samples?.length ?? 0) > MAX_SAMPLES_PER_BUCKET) {
      return {
        ok: false,
        code: "TOO_LARGE",
        message: `bucket "${bucket.typeName}" has ${bucket.samples.length} samples; max is ${MAX_SAMPLES_PER_BUCKET}.`,
      };
    }
  }

  // ISO-8601 date validation (T-06-01d).
  if (!isIsoDate(fh.workout.startDate) || !isIsoDate(fh.workout.endDate)) {
    return {
      ok: false,
      code: "INVALID_SHAPE",
      message: "workout startDate/endDate is not a parseable ISO-8601 date.",
    };
  }
  for (const event of fh.events ?? []) {
    if (!isIsoDate(event.date)) {
      return {
        ok: false,
        code: "INVALID_SHAPE",
        message: `event date "${event.date}" is not a parseable ISO-8601 date.`,
      };
    }
  }
  for (const activity of fh.activities ?? []) {
    if (!isIsoDate(activity.startDate)) {
      return {
        ok: false,
        code: "INVALID_SHAPE",
        message: `activity startDate "${activity.startDate}" is not a parseable ISO-8601 date.`,
      };
    }
    if (activity.endDate != null && !isIsoDate(activity.endDate)) {
      return {
        ok: false,
        code: "INVALID_SHAPE",
        message: `activity endDate "${activity.endDate}" is not a parseable ISO-8601 date.`,
      };
    }
    for (const event of activity.events ?? []) {
      if (!isIsoDate(event.date)) {
        return {
          ok: false,
          code: "INVALID_SHAPE",
          message: `activity event date "${event.date}" is not a parseable ISO-8601 date.`,
        };
      }
    }
  }
  for (const bucket of fh.buckets ?? []) {
    for (const sample of bucket.samples ?? []) {
      if (!isIsoDate(sample.startDate) || !isIsoDate(sample.endDate)) {
        return {
          ok: false,
          code: "INVALID_SHAPE",
          message: `sample in bucket "${bucket.typeName}" has a non-ISO-8601 date.`,
        };
      }
    }
  }

  // Metadata key-count / depth bound (T-06-01b).
  const tooBig = (bag: unknown) => !metadataWithinBounds(bag);
  for (const event of fh.events ?? []) {
    if (tooBig(event.metadata)) {
      return {
        ok: false,
        code: "TOO_LARGE",
        message: "event metadata exceeds the key-count/depth bound.",
      };
    }
  }
  for (const activity of fh.activities ?? []) {
    for (const event of activity.events ?? []) {
      if (tooBig(event.metadata)) {
        return {
          ok: false,
          code: "TOO_LARGE",
          message: "activity event metadata exceeds the key-count/depth bound.",
        };
      }
    }
  }
  for (const bucket of fh.buckets ?? []) {
    for (const sample of bucket.samples ?? []) {
      if (tooBig(sample.metadata)) {
        return {
          ok: false,
          code: "TOO_LARGE",
          message: `sample metadata in bucket "${bucket.typeName}" exceeds the key-count/depth bound.`,
        };
      }
    }
  }

  return null;
}

/**
 * Parse an imported firehose export string. THE untrusted-input boundary of
 * this milestone: Phase 6 reuses this exact path for companion firehose files
 * that originate on OTHER devices/people over CloudKit. Treat every input as
 * hostile — bound it, never throw on it, validate before returning it.
 *
 * Order matters and each step is a named STRIDE mitigation:
 *   1. SIZE BOUND (T-05-04a) — reject oversized input BEFORE JSON.parse.
 *   2. JSON.parse in try/catch (T-05-04d) — syntax errors become typed MALFORMED.
 *   3. Envelope vs legacy detection reads ONLY known fields off the parsed
 *      object; the untrusted object is never spread/Object.assign'd into an app
 *      target and its keys never index an app object (no prototype-pollution
 *      sink, T-05-04b).
 *   4. VERSION GATE (T-05-04c) — too-new / unrecognized schemaVersion is a typed
 *      UNSUPPORTED_SCHEMA_VERSION; only === CURRENT and the legacy v0 path pass.
 *   5. SHAPE VALIDATE (T-05-04d) — SwimFirehoseResponseSchema.safeParse (never
 *      .parse) gates the payload; failure is a typed INVALID_SHAPE carrying the
 *      first Zod issue.
 *
 * Not wired into any UI in this phase — no import flow exists yet (Phase 6 owns
 * that). This is a tested library function ready for Phase 6 to call.
 */
export function parseImport(input: string): ImportResult {
  // 1. SIZE BOUND — bound the raw input in UTF-8 BYTES before we parse anything
  // (T-05-04a). TextEncoder gives true byte length; `string.length` (UTF-16
  // code units) under-counts multibyte text and would let it bypass the cap.
  // The encode runs in microseconds on a sub-cap string and still precedes
  // JSON.parse, so the size-bound-before-parse invariant holds.
  const byteLen = new TextEncoder().encode(input).length;
  if (byteLen > MAX_IMPORT_BYTES) {
    return {
      ok: false,
      code: "TOO_LARGE",
      message: `Import is ${byteLen} bytes; max is ${MAX_IMPORT_BYTES}.`,
    };
  }

  // 2. PARSE — syntax errors become a typed result, never a throw (T-05-04d).
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch (err) {
    return {
      ok: false,
      code: "MALFORMED",
      message: err instanceof Error ? err.message : "Invalid JSON.",
    };
  }

  // Top-level must be a non-null object (rules out arrays, primitives, null).
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return {
      ok: false,
      code: "INVALID_SHAPE",
      message: "Top-level value is not an object.",
    };
  }

  // 3. ENVELOPE vs LEGACY — read ONLY known fields off the untrusted object.
  // A numeric `schemaVersion` => envelope; otherwise the whole object is a v0
  // legacy payload (the existing May-11 raw export). We never spread/assign the
  // untrusted object into an app target and never index app objects with its
  // keys (no prototype-pollution sink, T-05-04b).
  const rawVersion = (parsed as Record<string, unknown>).schemaVersion;
  let schemaVersion: number;
  let candidatePayload: unknown;

  if (typeof rawVersion === "number" && Number.isFinite(rawVersion)) {
    schemaVersion = rawVersion;
    candidatePayload = (parsed as Record<string, unknown>).payload;
  } else if (rawVersion === undefined) {
    // Legacy / no-envelope file: treat the whole object as a v0 payload.
    schemaVersion = 0;
    candidatePayload = parsed;
  } else {
    // schemaVersion present but not a finite number — unrecognized version.
    return {
      ok: false,
      code: "UNSUPPORTED_SCHEMA_VERSION",
      message: `schemaVersion must be a finite number; got ${typeof rawVersion}.`,
    };
  }

  // 4. VERSION GATE (T-05-04c) — fail loud on too-new / unrecognized versions.
  // Accept the legacy v0 path and the current version only.
  const versionRecognized =
    schemaVersion === 0 || schemaVersion === CURRENT_SCHEMA_VERSION;
  if (!versionRecognized) {
    return {
      ok: false,
      code: "UNSUPPORTED_SCHEMA_VERSION",
      message: `Unsupported schemaVersion ${schemaVersion}; this build supports up to ${CURRENT_SCHEMA_VERSION}.`,
    };
  }

  // 5. SHAPE VALIDATE (T-05-04d) — safeParse, never .parse, on untrusted input.
  const result = SwimFirehoseResponseSchema.safeParse(candidatePayload);
  if (!result.success) {
    const first = result.error.issues[0];
    const where = first?.path.join(".") || "(root)";
    return {
      ok: false,
      code: "INVALID_SHAPE",
      message: `Invalid payload at ${where}: ${first?.message ?? "unknown shape error"}.`,
    };
  }

  // 6. CONTENT HARDEN (Phase-6 pre-bakes, T-06-01a/b/d) — enforce array caps,
  // ISO-8601 dates, and metadata key-count/depth bounds on the SHAPE-VALID
  // payload BEFORE any consumer walks it. An under-byte-cap-but-pathological
  // file (millions of tiny samples, gibberish dates, key-count-flooded
  // metadata) is rejected here as a typed result, never a throw. Pure content
  // validation — still no identity/provenance awareness (T-06-01c).
  const hardenFailure = hardenContent(result.data as SwimFirehoseResponse);
  if (hardenFailure) return hardenFailure;

  // 7. SANITIZE + CLONE — never return the untrusted parse graph by reference.
  // structuredClone severs the alias; reserved metadata keys are stripped so a
  // companion file can't smuggle a prototype-pollution payload (T-05-04b).
  const sanitized = sanitizePayload(result.data as SwimFirehoseResponse);
  return { ok: true, payload: sanitized };
}
